import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import SpeechInputButton from "../components/SpeechInputButton";
import { apiFetch, assetUrl } from "../utils/api";
import { formatBeijingDateTime, formatRelativeTime } from "../utils/datetime";

function formatTime(iso) {
  const relative = formatRelativeTime(iso);
  if (!relative) return "";
  if (/前$|刚刚$/.test(relative)) return relative;
  return formatBeijingDateTime(iso, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function avatarUrl(url) {
  return assetUrl(url);
}

export default function MemberMomentsPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem("user_id");
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState("");

  const pageSize = 15;

  useSeo({
    title: "会员动态_会员日常分享 - AI婚恋平台",
    metaDescription: "会员动态：分享日常、生活片段与心情，增进彼此了解。",
    ogDescription: "会员日常动态分享。",
    keywords: "会员动态,日常分享,婚恋社区",
    path: "/moments",
  });

  const loadFeed = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(
        `/api/public/moments?page=${page}&page_size=${pageSize}`
      );
      const data = await res.json();
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("加载会员动态失败:", err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
  }, [page]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !userId) return;
    if (images.length + files.length > 9) {
      alert("最多上传 9 张图片");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("user_id", userId);
        form.append("file", file);
        const res = await apiFetch("/api/moments/upload-image", {
          method: "POST",
          body: form,
        });
        const data = await res.json();
        if (res.ok && data.url) {
          setImages((prev) => [...prev, data.url]);
        }
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const publish = async () => {
    if (!userId) {
      navigate("/login");
      return;
    }
    const text = content.trim();
    if (!text) {
      alert("请填写文字内容");
      return;
    }
    setPosting(true);
    try {
      const res = await apiFetch("/api/moments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: Number(userId), content: text, images }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "发布失败");
        return;
      }
      setContent("");
      setImages([]);
      setMessage("发布成功");
      setPage(1);
      loadFeed();
    } finally {
      setPosting(false);
    }
  };

  const removeOwn = async (momentId) => {
    if (!userId || !window.confirm("删除这条动态？")) return;
    const res = await apiFetch(`/api/moments/${momentId}?user_id=${userId}`, {
      method: "DELETE",
    });
    if (res.ok) loadFeed();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-pink-50 to-purple-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <div className="max-w-xl mx-auto space-y-6">
          <header className="text-center space-y-1">
            <h1 className="text-3xl font-bold text-gray-800">会员动态</h1>
            <p className="text-sm text-gray-500">分享日常，让彼此更真实、更亲近</p>
          </header>

          {userId ? (
            <section className="bg-white rounded-2xl shadow p-4 space-y-3">
              <div className="relative">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value.slice(0, 500))}
                  placeholder="这一刻的想法…也可点麦克风语音留言"
                  rows={4}
                  className="w-full border border-pink-100 rounded-xl px-3 py-2 pr-14 text-sm resize-none"
                />
                <div className="absolute right-2 bottom-2">
                  <SpeechInputButton
                    maxLength={500}
                    getPrefix={() => content}
                    onResult={(text) => {
                      if (text) setContent(String(text).slice(0, 500));
                    }}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 text-right">{content.length}/500</p>

              {images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {images.map((url, idx) => (
                    <div key={url} className="relative aspect-square">
                        <img src={assetUrl(url)} alt="" className="w-full h-full object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/50 text-white rounded-full text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleUpload}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || images.length >= 9}
                    className="text-sm text-pink-500 disabled:opacity-40"
                  >
                    {uploading ? "上传中..." : "添加图片"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={publish}
                  disabled={posting}
                  className="px-5 py-2 bg-pink-500 text-white rounded-full text-sm disabled:opacity-50"
                >
                  {posting ? "发布中..." : "发布"}
                </button>
              </div>
              {message && <p className="text-xs text-green-600">{message}</p>}
            </section>
          ) : (
            <section className="bg-white rounded-2xl shadow p-6 text-center text-sm text-gray-600">
              <p className="mb-3">登录后可发布动态</p>
              <Link to="/login" className="text-pink-500 hover:underline">
                去登录
              </Link>
            </section>
          )}

          {loading ? (
            <p className="text-center text-gray-400 py-12">加载中...</p>
          ) : items.length === 0 ? (
            <p className="text-center text-gray-400 py-12">还没有动态，来做第一个分享的人吧</p>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <article key={item.id} className="bg-white rounded-2xl shadow p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={avatarUrl(item.author_avatar) || "https://picsum.photos/40"}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{item.author_name}</p>
                      <p className="text-xs text-gray-400">
                        {[item.author_city, formatTime(item.created_at)].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    {userId && Number(userId) === item.user_id && (
                      <button
                        type="button"
                        onClick={() => removeOwn(item.id)}
                        className="text-xs text-gray-400 hover:text-red-500"
                      >
                        删除
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{item.content}</p>
                  {item.images?.length > 0 && (
                    <div
                      className={`grid gap-1 ${
                        item.images.length === 1
                          ? "grid-cols-1"
                          : item.images.length === 4
                          ? "grid-cols-2"
                          : "grid-cols-3"
                      }`}
                    >
                      {item.images.map((url) => (
                        <img
                          key={url}
                          src={assetUrl(url)}
                          alt=""
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
              >
                上一页
              </button>
              <span className="text-sm text-gray-500 self-center">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
